import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { loadLocalPlayers } from '../demoData';
import type { LoadState } from '../loadState';

export interface PlayerRow {
  id: string;
  name: string;
  position: string | null;
}

export type PlayersSource = 'live' | 'local';

interface TeamPlayersResult {
  rows: PlayerRow[];
  state: LoadState;
  source: PlayersSource;
  errorMessage?: string;
}

interface UseTeamPlayersOptions {
  limit?: number;
  /** Bump to force a re-fetch (e.g. after coach adds a player). */
  refreshKey?: number;
}

/**
 * Live + local players for a team. Live path hits Supabase; on empty or
 * throw we fall back to demo players seeded into localStorage. Same
 * empty-vs-error contract as `useTeamSessions`.
 */
export function useTeamPlayers(
  teamId: string | undefined,
  teamLoading: boolean,
  opts: UseTeamPlayersOptions = {},
): TeamPlayersResult {
  const limit = opts.limit ?? 40;
  const refreshKey = opts.refreshKey ?? 0;
  const [result, setResult] = useState<TeamPlayersResult>({
    rows: [],
    state: 'loading',
    source: 'live',
  });

  useEffect(() => {
    if (teamLoading) {
      setResult({ rows: [], state: 'loading', source: 'live' });
      return;
    }
    if (!teamId) {
      setResult({ rows: [], state: 'empty', source: 'live' });
      return;
    }
    let cancelled = false;
    setResult({ rows: [], state: 'loading', source: 'live' });

    const localFallback = (): PlayerRow[] =>
      loadLocalPlayers()
        .filter((p) => p.team_id === teamId)
        .map((p) => ({ id: p.id, name: p.name, position: p.position }));

    (async () => {
      let liveError: string | undefined;
      try {
        // Players live on `team_players` joined with `users` per migration
        // 00008 — there is no standalone `players` table in the live DB
        // schema. team_players carries (player_id, position) per team;
        // users.name is the display name.
        const { data, error } = await supabase
          .from('team_players')
          .select('player_id, position, users!inner(name)')
          .eq('team_id', teamId)
          .eq('is_active', true)
          .limit(limit);
        if (cancelled) return;
        if (error) {
          liveError = error.message;
        } else {
          const rows: PlayerRow[] = (data ?? []).map((r: any) => ({
            id: r.player_id as string,
            name: (r.users?.name as string | undefined) ?? '',
            position: (r.position as string | null) ?? null,
          })).sort((a, b) => a.name.localeCompare(b.name));
          if (rows.length > 0) {
            setResult({ rows, state: 'ok', source: 'live' });
            return;
          }
        }
      } catch (err) {
        if (cancelled) return;
        liveError = err instanceof Error ? err.message : 'fetch failed';
      }

      const local = localFallback();
      if (local.length > 0) {
        setResult({ rows: local, state: 'ok', source: 'local' });
        return;
      }
      setResult(
        liveError
          ? { rows: [], state: 'error', source: 'live', errorMessage: liveError }
          : { rows: [], state: 'empty', source: 'live' },
      );
    })();
    return () => { cancelled = true; };
  }, [teamId, teamLoading, limit, refreshKey]);

  return result;
}
